import type { MenuNavigationContextValue } from "../../context/MenuNavigationContext";
import type { AppSession } from "@repo/utils";
import {
  ROUTES,
  ROUTESWEB,
  collectExamIdsFromEnrolledProgram,
  getGradeIdFromEnrolledProgram,
  getVerticalIdFromEnrolledProgram,
} from "@repo/utils";

type ProgramLike = Parameters<typeof getGradeIdFromEnrolledProgram>[0];

export function isWebTestsDrawerPath(pathname: string): boolean {
  const segment = ROUTESWEB.APP.TESTS;
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const base = `/${segment}`;
  return normalized === base || normalized.startsWith(`${base}/`);
}

const TEST_ROUTE_NAMES = new Set<string>(
  Object.values(ROUTES.APP.TESTS) as string[],
);

function focusedRouteChainMatchesTests(state: unknown): boolean {
  if (!state || typeof state !== "object") return false;
  const routes = (state as { routes?: unknown[] }).routes;
  const index =
    typeof (state as { index?: number }).index === "number"
      ? (state as { index: number }).index
      : 0;

  if (!Array.isArray(routes) || routes[index] == null) return false;

  const route = routes[index] as { name?: string; state?: unknown };
  const name = route.name;

  if (route.state != null) {
    if (focusedRouteChainMatchesTests(route.state)) return true;
  }

  if (name != null && TEST_ROUTE_NAMES.has(name)) {
    return true;
  }

  return false;
}

export function navigationIncludesActiveTestsRoute(
  navigation?: {
    getState?: () => unknown;
    getRootState?: () => unknown;
    getParent?: () => unknown;
  } | null,
): boolean {
  if (!navigation) return false;
  try {
    const tryState = navigation.getRootState?.() ?? navigation.getState?.();
    if (focusedRouteChainMatchesTests(tryState)) return true;

    let parent = navigation.getParent?.() as typeof navigation | undefined;
    while (parent) {
      const st =
        typeof parent === "object" &&
        parent &&
        typeof (parent as { getState?: () => unknown }).getState === "function"
          ? (parent as { getState: () => unknown }).getState()
          : undefined;
      if (focusedRouteChainMatchesTests(st)) return true;
      parent = (parent as { getParent?: () => unknown }).getParent?.() as
        | typeof navigation
        | undefined;
    }
  } catch {
    return false;
  }
  return false;
}
export function resolveMenuDrawerVisibilityArgs(
  ctx: MenuNavigationContextValue,
  patch: Partial<AppSession>,
  opts: { applyPatchOverlay: boolean },
): {
  gradeId: number | null;
  examIds: readonly number[];
  verticalId: number | null;
} {
  const { applyPatchOverlay } = opts;
  const enrolled = patch.enrolledPrograms;

  if (!applyPatchOverlay || !Array.isArray(enrolled) || enrolled.length === 0) {
    return {
      gradeId: ctx.gradeId,
      examIds: ctx.examIds,
      verticalId: ctx.verticalId ?? null,
    };
  }

  const pid =
    patch.programId != null && String(patch.programId).length > 0
      ? String(patch.programId)
      : null;
  const rows = enrolled as ProgramLike[];

  const program =
    (pid != null
      ? rows.find(
          (p) =>
            String((p as { program_id?: string })?.program_id ?? "") === pid,
        )
      : undefined) ??
    rows.find((p) => Boolean((p as { isDefault?: boolean })?.isDefault)) ??
    rows[0];

  const g = getGradeIdFromEnrolledProgram(program);
  const exams = collectExamIdsFromEnrolledProgram(program);
  const v = getVerticalIdFromEnrolledProgram(program);

  const pg = patch.gradeId;
  const patchGradeOk =
    pg != null && Number.isFinite(Number(pg)) && Number(pg) > 0;

  const pv = patch.verticalId;
  const patchVerticalOk =
    pv != null && Number.isFinite(Number(pv)) && Number(pv) > 0;

  const patchExamRaw = patch.examId ?? patch.mdsExamId;
  const patchExamN = Number(patchExamRaw);
  const patchExamsOk = Number.isFinite(patchExamN) && patchExamN > 0;

  const gradeResolved = g ?? (patchGradeOk ? Number(pg) : null) ?? ctx.gradeId;
  const examsResolved =
    exams.length > 0 ? exams : patchExamsOk ? [patchExamN] : [...ctx.examIds];
  const verticalResolved =
    v ?? (patchVerticalOk ? Number(pv) : null) ?? ctx.verticalId ?? null;

  return {
    gradeId: gradeResolved,
    examIds: examsResolved,
    verticalId: verticalResolved,
  };
}
